import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  Paper,
  Alert,
  Snackbar,
  IconButton,
  Divider
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import AvailabilityCalendar from './AvailabilityCalendar';
import { ChevronLeft, ChevronRight, Close, Fullscreen } from '@mui/icons-material';
import { getImagePath } from '../utils/imageUtils';
import {
  parseFeatureSections,
  splitDescriptionParagraphs,
  splitSectionBodyToItems
} from '../utils/featureTextFormat';
import { EquipmentFeatureIcon } from '../utils/equipmentIcons';

const YachtDetails = () => {
  const { id } = useParams();
  const [yacht, setYacht] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const imagePaths = useMemo(
    () => getImagePath(yacht?.imageUrls || yacht?.imageNames || []),
    [yacht]
  );
  const imageArray = useMemo(
    () => (Array.isArray(imagePaths) ? imagePaths : [imagePaths]),
    [imagePaths]
  );

  useEffect(() => {
    const fetchYacht = async () => {
      try {
        const yachtDoc = await getDoc(doc(db, 'yachts', id));
        if (yachtDoc.exists()) {
          setYacht({ id: yachtDoc.id, ...yachtDoc.data() });
        } else {
          setError('Nie znaleziono jachtu');
        }
      } catch (error) {
        setError('Błąd podczas ładowania danych jachtu');
      } finally {
        setLoading(false);
      }
    };

    fetchYacht();
  }, [id]);

  const handlePreviousImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? imageArray.length - 1 : prevIndex - 1
    );
  }, [imageArray.length]);

  const handleNextImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === imageArray.length - 1 ? 0 : prevIndex + 1
    );
  }, [imageArray.length]);

  const handleOpenFullscreen = () => {
    setFullscreenOpen(true);
  };

  const handleCloseFullscreen = () => {
    setFullscreenOpen(false);
  };

  // Handle keyboard navigation in fullscreen
  useEffect(() => {
    if (!fullscreenOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCloseFullscreen();
      } else if (e.key === 'ArrowLeft') {
        handlePreviousImage();
      } else if (e.key === 'ArrowRight') {
        handleNextImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreenOpen, handleCloseFullscreen, handleNextImage, handlePreviousImage]);


  if (loading) return <Container><Typography>Ładowanie...</Typography></Container>;
  if (!yacht) return <Container><Typography>Nie znaleziono jachtu</Typography></Container>;

  const descriptionParagraphs = (() => {
    const parts = splitDescriptionParagraphs(yacht.description || '');
    if (parts.length) return parts;
    const single = (yacht.description || '').trim();
    return single ? [single] : [];
  })();
  const equipmentSections = parseFeatureSections(yacht.features || '');

  return (
    <Box sx={{ pt: 2 }}>
      <Container
        maxWidth="lg"
        sx={{
          mt: 4,
          mb: 4
        }}
      >
        {error && (
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError('')}
          >
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          </Snackbar>
        )}

        <Grid container spacing={{ xs: 3, md: 4 }}>
          <Grid item xs={12} md={8}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                backgroundColor: 'white'
              }}
            >
              <Typography 
                variant="h4" 
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: '#1976d2',
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' }
                }}
              >
                {yacht.name}
              </Typography>
            
            <Box sx={{ 
              position: 'relative', 
              width: '100%', 
              height: { xs: 250, sm: 350, md: 400 }, 
              mb: 3,
              borderRadius: 2,
              overflow: 'hidden',
              cursor: 'pointer'
            }}
            onClick={handleOpenFullscreen}
            >
              <img
                src={imageArray[currentImageIndex] || '/images/yachts/default-yacht.jpg'}
                alt={`${yacht.name} - zdjęcie ${currentImageIndex + 1}`}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  borderRadius: 8,
                  filter: 'blur(0.5px)',
                  imageRendering: 'smooth',
                  WebkitFontSmoothing: 'antialiased'
                }}
                onError={(e) => {
                  e.target.src = '/images/yachts/default-yacht.jpg';
                }}
              />
              <IconButton
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenFullscreen();
                }}
                title="Pełny ekran"
              >
                <Fullscreen />
              </IconButton>
              
              {imageArray.length > 1 && (
                <>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                      zIndex: 2
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviousImage();
                    }}
                  >
                    <ChevronLeft />
                  </IconButton>
                  
                  <IconButton
                    sx={{
                      position: 'absolute',
                      right: 48,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                      zIndex: 2
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextImage();
                    }}
                  >
                    <ChevronRight />
                  </IconButton>
                  
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      gap: 1
                    }}
                  >
                    {imageArray.map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: index === currentImageIndex ? 'primary.main' : 'grey.300',
                          cursor: 'pointer'
                        }}
                        onClick={() => setCurrentImageIndex(index)}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Box>

              <Typography 
                variant="h6" 
                gutterBottom
                sx={{
                  fontWeight: 600,
                  color: '#333',
                  mt: 3
                }}
              >
                Opis
              </Typography>
              {descriptionParagraphs.map((paragraph, idx) => (
                <Typography
                  key={idx}
                  component="p"
                  sx={{
                    color: '#555',
                    lineHeight: 1.75,
                    fontSize: '1.05rem',
                    mb: 2,
                    whiteSpace: 'pre-wrap',
                    '&:last-of-type': { mb: 0 }
                  }}
                >
                  {paragraph}
                </Typography>
              ))}
              
              <Typography 
                variant="h6" 
                sx={{
                  fontWeight: 600,
                  color: '#333',
                  mt: 4,
                  mb: 2
                }}
              >
                Specyfikacja
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      height: '100%',
                      bgcolor: 'grey.50',
                      borderColor: 'divider'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Długość
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                      {yacht.length} m
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      height: '100%',
                      bgcolor: 'grey.50',
                      borderColor: 'divider'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Pojemność
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                      {yacht.capacity} osób
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      height: '100%',
                      bgcolor: 'grey.50',
                      borderColor: 'divider'
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Lokalizacja
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.1rem' }}>
                      {yacht.location}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {(yacht.features || '').trim() ? (
                <Box sx={{ mt: 1 }}>
                  <Divider sx={{ my: 3 }} />

                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: 700,
                      color: '#1a1a1a',
                      fontSize: { xs: '1.2rem', sm: '1.35rem' },
                      mb: 3
                    }}
                  >
                    Wyposażenie
                  </Typography>

                  {equipmentSections.map((section, idx) => {
                    const items = splitSectionBodyToItems(section.body);
                    const isLongProse =
                      items.length === 1 && items[0] && items[0].length > 360;

                    return (
                      <Box key={idx}>
                        {idx > 0 && <Divider sx={{ my: 4, borderColor: 'grey.200' }} />}

                        {section.title && (
                          <Typography
                            variant="h6"
                            component="h3"
                            sx={{
                              fontWeight: 600,
                              fontSize: '1.1rem',
                              color: 'text.primary',
                              mb: 2.5,
                              letterSpacing: 0.01
                            }}
                          >
                            {section.title}
                          </Typography>
                        )}

                        {items.length === 0 && section.body?.trim() ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.75, whiteSpace: 'pre-wrap' }}
                          >
                            {section.body}
                          </Typography>
                        ) : isLongProse ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.75, whiteSpace: 'pre-wrap' }}
                          >
                            {items[0]}
                          </Typography>
                        ) : (
                          <Grid container spacing={2} columnSpacing={{ xs: 2, sm: 4 }}>
                            {items.map((item, i) => (
                              <Grid item xs={12} sm={6} key={`${idx}-${i}`}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.75,
                                    py: 0.75,
                                    pr: { sm: 1 }
                                  }}
                                >
                                  <EquipmentFeatureIcon label={item} />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.primary',
                                      lineHeight: 1.55,
                                      fontWeight: 400,
                                      pt: 0.2
                                    }}
                                  >
                                    {item}
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : null}
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <AvailabilityCalendar yacht={yacht} />
          </Grid>
        </Grid>
      </Container>

      {/* Fullscreen Image Modal */}
      {fullscreenOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
          onClick={handleCloseFullscreen}
        >
          <IconButton
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
              zIndex: 10000
            }}
            onClick={handleCloseFullscreen}
            size="large"
          >
            <Close />
          </IconButton>

          {imageArray.length > 1 && (
            <>
              <IconButton
                sx={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                  zIndex: 10000
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreviousImage();
                }}
                size="large"
              >
                <ChevronLeft fontSize="large" />
              </IconButton>
              
              <IconButton
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                  zIndex: 10000,
                  mr: 8
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleNextImage();
                }}
                size="large"
              >
                <ChevronRight fontSize="large" />
              </IconButton>

              <Box
                sx={{
                  position: 'absolute',
                  bottom: 24,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 1,
                  zIndex: 10000
                }}
              >
                {imageArray.map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: index === currentImageIndex ? 'white' : 'rgba(255, 255, 255, 0.5)',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentImageIndex(index);
                    }}
                  />
                ))}
              </Box>
            </>
          )}

          <Box
            sx={{
              maxWidth: '95vw',
              maxHeight: '95vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={imageArray[currentImageIndex] || '/images/yachts/default-yacht.jpg'}
              alt={`${yacht.name} - zdjęcie ${currentImageIndex + 1}`}
              style={{ 
                maxWidth: '100%',
                maxHeight: '95vh',
                objectFit: 'contain',
                filter: 'blur(0.5px)',
                imageRendering: 'smooth',
                WebkitFontSmoothing: 'antialiased'
              }}
              onError={(e) => {
                e.target.src = '/images/yachts/default-yacht.jpg';
              }}
            />
          </Box>

          <Typography
            sx={{
              position: 'absolute',
              bottom: imageArray.length > 1 ? 60 : 24,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              fontSize: '0.875rem',
              zIndex: 10000
            }}
          >
            {currentImageIndex + 1} / {imageArray.length}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default YachtDetails; 